// "unmask" is used to let FullStory capture the component
//   see: https://help.fullstory.com/hc/en-us/articles/360044349073-FullStory-Private-by-Default#01FHE22KSQ9J1N7DZ685G3M872
import { useRef, type InputHTMLAttributes } from 'react';
import Icon, { RemixIconNames } from '~/components/ui/Icon';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';

export type CheckBoxProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value' | 'form'
> & {
    label?: string;
    value?: boolean;
    unmask?: boolean;
    className?: string;
    onChange?: (value: boolean) => void;
} & DataTestAttributes;

export type CheckBoxInputProps = {
    unmask?: boolean;
    className?: string;
    checked?: boolean;
    onChange?: (value: boolean) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'checked'> &
    DataTestAttributes;

export function CheckBoxInput({
    onChange,
    unmask = false,
    className = '',
    checked = false,
    'data-test-component': testComponent = 'ui/forms/CheckBoxInput',
    'data-test-name': testName,
    'data-test-id': testId,
    ...props
}: CheckBoxInputProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <div
            className={`checkbox-input-control ${className}`}
            onClick={(e) => {
                // ignore input’s own click when bubbles back up after inputRef.current?.click()
                // ignore also the click when readOnly is set. Notice this is not the same readonly for the input, this
                // is to make the element to display a state but not to be interactive.
                if (e.target === inputRef.current || props.readOnly) return;
                inputRef.current?.click();
            }}
            data-test-component={testComponent}
            data-test-name={testName}
            data-test-id={testId}>
            {checked ? (
                <Icon
                    icon={RemixIconNames.RiCheckboxFill}
                    className='checkbox-input-icon-active'
                />
            ) : (
                <Icon
                    icon={RemixIconNames.RiSquareLine}
                    className='checkbox-input-icon-inactive'
                />
            )}
            <input
                {...props}
                ref={inputRef}
                type='checkbox'
                className={unmask ? 'fs-unmask' : ''}
                onChange={() => onChange?.(!checked)}
                checked={checked}
                readOnly
            />
        </div>
    );
}
export default function CheckBox({
    label,
    id,
    value,
    onChange,
    unmask,
    className = '',
    'data-test-component': testComponent = 'ui/forms/CheckBox',
    'data-test-name': testName,
    'data-test-id': testId,
    ...props
}: CheckBoxProps) {
    return (
        <label
            className={classNames({ 'checkbox-input': true, [className]: Boolean(className) })}
            htmlFor={id}
            data-test-component={testComponent}
            data-test-name={testName}
            data-test-id={testId}>
            <CheckBoxInput
                {...props}
                id={id}
                checked={Boolean(value)}
                className={classNames({
                    'checkbox-input-btn': true,
                    'checkbox-input-filled': Boolean(value),
                })}
                unmask={unmask}
                onChange={onChange}
                readOnly
            />
            {label && <span>{label}</span>}
        </label>
    );
}
